/* TODO: t() */
import React, { useState, useMemo } from 'react';
import { ScrollView, StyleSheet } from 'react-native';
import { useHistory } from 'react-router-dom';
import { gql, useQuery } from '@apollo/client';
import { Checkbox } from '@chakra-ui/react';

import { Button, TextInput, Modal } from '../uiold';
import { themeColors } from '../uiold/Constants';
import LoadingIndicator from '../uiold/LoadingIndicator';
import { loginAsAccount } from '../../support/loginAsAccount';
import IndexedList from '../../support/shared/IndexedList';

import { showError } from './AppAlert';
import {
  GetAccounts,
  GetAccounts_accounts as Account,
} from './__generated__/GetAccounts';

const GET_ACCOUNTS = gql`
  query GetAccounts {
    accounts {
      id
      name
      suspended
    }
  }
`;

type Props = {
  accountId: string;
  onHide: () => void;
};

export default function LoginAsDialog(props: Props) {
  const { accountId, onHide } = props;
  const [filterText, setFilterText] = useState('');
  const [accounts, setAccounts] = useState(new IndexedList<Account>([]));
  const [includeSuspendedAccounts, setIncludeSuspendedAccounts] = useState(
    false,
  );
  const history = useHistory();

  const { loading } = useQuery<GetAccounts>(GET_ACCOUNTS, {
    fetchPolicy: 'cache-and-network',
    onCompleted(data) {
      setAccounts(new IndexedList(data.accounts));
    },
    onError(error) {
      // eslint-disable-next-line no-console
      console.error(error);
      showError('Failed to fetch');
    },
  });

  const visibleAccounts = useMemo(() => {
    return includeSuspendedAccounts
      ? accounts
      : accounts.filter((account) => !account.suspended);
  }, [accounts, includeSuspendedAccounts]);

  const filteredAccounts = useMemo(() => {
    const text = filterText.trim().toLowerCase();
    return visibleAccounts.filter(
      ({ id, name }) =>
        id !== accountId && name != null && name.toLowerCase().includes(text),
    );
  }, [filterText, visibleAccounts, accountId]);

  const sortedAccounts = useMemo(() => {
    return filteredAccounts.sortByKey('name');
  }, [filteredAccounts]);

  const suspendedAccountCount = accounts.filter(
    (account) => !!account.suspended,
  ).length;

  return (
    <Modal isVisible title="Login As" onClose={onHide} style={styles.modal}>
      <Modal.Body style={styles.modalBody}>
        <TextInput
          type="search"
          placeholder="Type to Search"
          autoFocus
          value={filterText}
          onChangeText={(text) => setFilterText(text)}
          style={styles.searchBar}
        />
        <Checkbox
          mb={5}
          isChecked={includeSuspendedAccounts}
          onChange={(e) => setIncludeSuspendedAccounts(e.target.checked)}
        >
          Include Suspended Accounts ({suspendedAccountCount})
        </Checkbox>
        <ScrollView style={styles.scrollView}>
          {loading ? (
            <LoadingIndicator />
          ) : (
            sortedAccounts.mapToArray((account) => (
              <Button
                key={account.id}
                title={
                  account.suspended
                    ? `${account.name} (Suspended)`
                    : account.name
                }
                onPress={() => {
                  onHide();
                  loginAsAccount(account.id, {
                    onSuccess: () => history.push('/'),
                  });
                }}
                style={styles.accountButton}
                hoverStyle={styles.active}
              />
            ))
          )}
        </ScrollView>
      </Modal.Body>
    </Modal>
  );
}

let styles = StyleSheet.create({
  modal: {
    width: 640,
  },
  modalBody: {
    marginBottom: 0,
  },
  scrollView: {
    height: '60vh',
  },
  searchBar: {
    backgroundColor: themeColors.lightGrey,
    marginBottom: 20,
  },
  accountButton: {
    borderWidth: 0,
    justifyContent: 'flex-start',
    paddingVertical: 12,
  },
  active: {
    backgroundColor: themeColors.withOpacity.blue8,
  },
});
